/**
 * useEffectivePermissions — the caller's effective permission keys + wildcard
 * flag (Story UI-U6 AC2), the single source of guard-awareness for the whole
 * Administration area.
 *
 * The web renders nav items / pages / actions from the api's answer and NEVER
 * recomputes auth rules (coding-standards rule #2) — the api guards remain the
 * hard line. `can(key)` mirrors the api's `hasPermission`: `is_wildcard ||
 * keys.includes(key)` (the wildcard admin carries an EMPTY key set).
 *
 * live mode → GET /me/effective-permissions via the typed client.
 * mock mode → wildcard fixture (every screen renders standalone in tests / dev;
 *             the api guard is what actually enforces access at runtime).
 *
 * [Source: apps/web/src/features/carriers/hooks/useCarrierRows.ts] (live/mock split)
 * [Source: apps/api/src/auth/auth-context.ts#hasPermission]
 */

import { useQuery } from '@tanstack/react-query';
import type { EffectivePermissionsResponse } from '@brokly/shared';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import { apiClient } from '../../lib/api-client';

/** Mock-mode fixture — wildcard admin (renders the full admin area in tests/dev). */
export const EFFECTIVE_PERMISSIONS_FIXTURE: EffectivePermissionsResponse = {
  permission_keys: [],
  is_wildcard: true,
};

export interface EffectivePermissions {
  /** Test a single permission key (mirrors the api's hasPermission). */
  can: (key: string) => boolean;
  /** True when the caller holds ANY of the given keys (empty list ⇒ no requirement ⇒ true). */
  canAny: (keys: readonly string[]) => boolean;
  /** The raw wildcard flag. */
  isWildcard: boolean;
  /** The raw (sorted) key list — empty when wildcard. */
  keys: string[];
  isLoading: boolean;
  isError: boolean;
}

export function useEffectivePermissions(): EffectivePermissions {
  const live = isLiveData();
  const query = useQuery<EffectivePermissionsResponse>({
    queryKey: qk.effectivePermissions(),
    queryFn: () =>
      live ? apiClient.admin.effectivePermissions() : Promise.resolve(EFFECTIVE_PERMISSIONS_FIXTURE),
    staleTime: live ? 5 * 60_000 : Infinity,
    ...(live ? {} : { initialData: EFFECTIVE_PERMISSIONS_FIXTURE }),
  });

  const data = query.data ?? { permission_keys: [], is_wildcard: false };
  const isWildcard = data.is_wildcard;
  const keys = data.permission_keys;

  const can = (key: string): boolean => isWildcard || keys.includes(key);
  const canAny = (requiredKeys: readonly string[]): boolean =>
    requiredKeys.length === 0 || requiredKeys.some((k) => can(k));

  return {
    can,
    canAny,
    isWildcard,
    keys,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
